import { useMutation, useTRPC } from "@luckymenu/api-client/react";
import type { ModifierGroup } from "@luckymenu/core";
import { useCallback, useMemo, useState } from "react";
import {
	type AddonGroupDraft,
	addOption,
	addonGroupDraftToInput,
	addonGroupToDraft,
	moveOption,
	newAddonGroupDraft,
	type OptionDraft,
	removeOption,
	setSelect,
	toggleItem,
	updateOption,
} from "../menu/addon-form";
import { withGroupItems, withModifierGroup, withoutModifierGroup } from "../menu/cache";
import type { FormErrors } from "../menu/form-errors";
import { type MenuProblem, menuProblemOf } from "../menu/problems";
import { itemsWithGroup } from "../menu/view";
import { useMenu, useMenuCache } from "./use-menu";
import { useStoredDraft } from "./use-stored-draft";

const newBase = (draft: AddonGroupDraft) =>
	JSON.stringify({ ...draft, id: "", options: draft.options.map((o) => ({ ...o, id: "" })) });
const editBase = (draft: AddonGroupDraft) => JSON.stringify(draft);

/**
 * The add-on group form, shared by both apps: the group, its options and the
 * items that offer it. `groupId` to edit, or null for a new group. Saving
 * sends the group, then the item list if it changed. Needs `menu.edit`.
 */
export function useAddonGroupForm({ groupId }: { groupId: string | null }) {
	const trpc = useTRPC();
	const menu = useMenu();
	const cache = useMenuCache();
	const [errors, setErrors] = useState<FormErrors>({});
	const [problem, setProblem] = useState<MenuProblem | null>(null);

	const original: ModifierGroup | null = useMemo(
		() => (groupId ? (menu.data?.modifierGroups.find((g) => g.id === groupId) ?? null) : null),
		[menu.data, groupId],
	);
	const originalItemIds = useMemo(
		() => (menu.data && groupId ? itemsWithGroup(menu.data, groupId).map((i) => i.id) : []),
		[menu.data, groupId],
	);
	const menuOrder = useMemo(() => menu.data?.items.map((i) => i.id) ?? [], [menu.data]);
	const loaded = menu.data !== undefined;
	const initial = useMemo(() => {
		if (groupId) return original ? addonGroupToDraft(original, originalItemIds) : null;
		return loaded ? newAddonGroupDraft() : null;
	}, [groupId, original, originalItemIds, loaded]);
	const stored = useStoredDraft(
		groupId ? `addon:${groupId}` : "addon:new",
		initial,
		groupId ? editBase : newBase,
	);
	const draft = stored.draft;

	const edit = useCallback(
		(change: (d: AddonGroupDraft) => AddonGroupDraft) => {
			setProblem(null);
			stored.update(change);
		},
		[stored.update],
	);
	const clear = (...keys: string[]) =>
		setErrors((e) => {
			if (!keys.some((k) => k in e)) return e;
			const next = { ...e };
			for (const k of keys) delete next[k];
			return next;
		});

	const save = useMutation(trpc.staff.catalog.upsertModifierGroup.mutationOptions());
	const attach = useMutation(trpc.staff.catalog.setModifierGroupItems.mutationOptions());
	const remove = useMutation(trpc.staff.catalog.deleteModifierGroup.mutationOptions());

	return {
		status: menu.isPending
			? ("loading" as const)
			: groupId && !original
				? ("notFound" as const)
				: !draft
					? ("loading" as const)
					: ("ready" as const),
		isNew: groupId === null,
		original,
		draft,
		restored: stored.restored,
		dirty: stored.dirty,
		errors,
		problem,
		saving: save.isPending || attach.isPending,
		deleting: remove.isPending,
		/** Items to choose from, and categories to group them by. */
		menu: menu.data,

		setName: (name: string) => {
			clear("name");
			edit((d) => ({ ...d, name }));
		},
		/** How many options a diner must and may pick. */
		setSelect: (field: "minSelect" | "maxSelect", value: number) => {
			clear("minSelect", "maxSelect");
			edit((d) => setSelect(d, field, value));
		},
		addOption: () => {
			clear("options", "maxSelect");
			edit(addOption);
		},
		removeOption: (index: number) => {
			setErrors((e) =>
				Object.fromEntries(Object.entries(e).filter(([k]) => !k.startsWith("options."))),
			);
			edit((d) => removeOption(d, index));
		},
		updateOption: (index: number, change: Partial<Omit<OptionDraft, "id">>) => {
			clear(...Object.keys(change).map((k) => `options.${index}.${k}`));
			edit((d) => updateOption(d, index, change));
		},
		moveOption: (from: number, to: number) => edit((d) => moveOption(d, from, to)),
		toggleItem: (itemId: string) => edit((d) => toggleItem(d, itemId, menuOrder)),
		/** Offers the group on exactly these items, such as every item of a category. */
		setItems: (itemIds: readonly string[]) => {
			const chosen = new Set(itemIds);
			edit((d) => ({ ...d, itemIds: menuOrder.filter((id) => chosen.has(id)) }));
		},
		discard: () => {
			setErrors({});
			setProblem(null);
			stored.reset();
		},

		submit: async (): Promise<ModifierGroup | null> => {
			if (!draft) return null;
			const result = addonGroupDraftToInput(draft);
			if (!result.ok) {
				setErrors(result.errors);
				return null;
			}
			setErrors({});
			setProblem(null);
			try {
				const group = await save.mutateAsync(result.input);
				cache.update((m) => withModifierGroup(m, group));
				const itemsChanged =
					groupId === null || JSON.stringify(result.itemIds) !== JSON.stringify(originalItemIds);
				if (itemsChanged) {
					const attached = await attach.mutateAsync({ groupId: group.id, itemIds: result.itemIds });
					cache.update((m) => withGroupItems(m, group.id, attached.itemIds));
				}
				if (groupId) stored.reset(addonGroupToDraft(group, result.itemIds));
				else stored.forget();
				return group;
			} catch (error) {
				setProblem(menuProblemOf(error));
				return null;
			}
		},

		delete: async (): Promise<boolean> => {
			if (!groupId) return false;
			try {
				await remove.mutateAsync({ id: groupId });
				stored.forget();
				cache.update((m) => withoutModifierGroup(m, groupId));
				return true;
			} catch (error) {
				setProblem(menuProblemOf(error));
				return false;
			}
		},
	};
}

export type AddonGroupForm = ReturnType<typeof useAddonGroupForm>;
